import React from "react";

/**
 * Weekly Brier trend chart — AI vs Market over time.
 * Lower = better. Dashed = market baseline.
 */
export default function TrendChart({ items }) {
  if (!items || items.length === 0) return null;

  const W = 760;
  const H = 240;
  const PAD = { top: 24, right: 24, bottom: 44, left: 48 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  const allValues = items.flatMap((d) => [d.aiBrier, d.marketBrier]).filter((v) => v != null);
  const minV = 0;
  const maxV = Math.max(0.25, Math.max(...allValues) * 1.15);

  const x = (i) => (items.length <= 1 ? PAD.left + innerW / 2 : PAD.left + (i / (items.length - 1)) * innerW);
  const y = (v) => PAD.top + innerH - ((v - minV) / (maxV - minV)) * innerH;

  const aiPath = items.map((d, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(2)} ${y(d.aiBrier).toFixed(2)}`).join(" ");
  const mktPath = items.map((d, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(2)} ${y(d.marketBrier).toFixed(2)}`).join(" ");
  const yTicks = [0, maxV * 0.25, maxV * 0.5, maxV * 0.75, maxV];

  // Show at most ~8 x-axis labels
  const xStride = Math.max(1, Math.ceil(items.length / 8));

  return (
    <div data-testid="trend-chart" className="w-full">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="auto" className="block">
        {yTicks.map((t, i) => (
          <g key={i}>
            <line
              x1={PAD.left} x2={PAD.left + innerW} y1={y(t)} y2={y(t)}
              stroke="rgba(255,255,255,0.06)" strokeWidth="1"
            />
            <text
              x={PAD.left - 8} y={y(t) + 3}
              textAnchor="end" fill="#71717a"
              fontFamily="IBM Plex Mono, monospace" fontSize="10"
            >
              {t.toFixed(2)}
            </text>
          </g>
        ))}
        {items.map((d, i) =>
          i % xStride === 0 || i === items.length - 1 ? (
            <text
              key={i}
              x={x(i)} y={H - PAD.bottom + 16}
              textAnchor="middle" fill="#71717a"
              fontFamily="IBM Plex Mono, monospace" fontSize="10"
            >
              {d.weekKey}
            </text>
          ) : null
        )}

        <text
          x={14} y={PAD.top + innerH / 2}
          textAnchor="middle" fill="#a1a1aa"
          fontFamily="IBM Plex Mono, monospace" fontSize="10"
          letterSpacing="2"
          transform={`rotate(-90, 14, ${PAD.top + innerH / 2})`}
        >
          BRIER SCORE
        </text>

        {/* Market baseline */}
        <path d={mktPath} fill="none" stroke="#a1a1aa" strokeWidth="1.5" strokeDasharray="4 4" />
        {/* AI line */}
        <path d={aiPath} fill="none" stroke="#00d8ff" strokeWidth="2" />

        {items.map((d, i) => (
          <g key={i}>
            <circle cx={x(i)} cy={y(d.aiBrier)} r="3" fill="#00d8ff" />
            <circle cx={x(i)} cy={y(d.marketBrier)} r="2.5" fill="#a1a1aa" />
          </g>
        ))}
      </svg>
      <div className="mt-3 text-[10px] uppercase tracking-[0.22em] font-mono text-zinc-500 flex items-center gap-5">
        <span className="inline-flex items-center gap-2">
          <span className="inline-block h-[2px] w-5 bg-edge" /> MFSMAI Oracle
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="inline-block h-[2px] w-5 border-t border-dashed border-zinc-400" /> Polymarket
        </span>
        <span className="text-zinc-500">· Lower Brier is better</span>
      </div>
    </div>
  );
}
